
import React from "react";
import { Grid } from "semantic-ui-react";
import { handleAddressValue, operationOfNumberArray } from "../../Helpers";
import { getIsWhiteLabeled } from "../../helpers/PermissionHelper";
import { BrandStyles, getBrandedStyle } from "../constants/BrandingConstants";
import { ConfigDefaults } from "../constants/BrokerConstants";
import { ErrorMessages, GlobalConstants, YesNoBooleanRadios } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import BCFormInput from "../ui/BCFormInput";
import BCValidationMessage from "../ui/BCValidationMessage";
import { BPQuestionLabels } from "./BPQuestionLabels";

interface IBPAssociatedBrokerQuestion {
  obj: any;
  handleFormChange: (value: any, field: any) => void;
  disabled?: boolean;
  invalidProps: string[];
  flow: string;
}

interface IBPBrokerEmailQuestion {
  isBrokerSelected: boolean;
  handleAddBroker: () => void;
  handleOnSearchChange: (value: any, field: any, charLimit?: number | undefined) => Promise<void>;
  obj: any;
  value: string;
  handleRenderSuggestions: () => any[];
  handleSearchSelect: (result: any) => Promise<void>;
  isSearchOpen: boolean;
  onSearchFocus: (value: React.SetStateAction<boolean>) => void;
  handleSearchKeyDown: (e: any) => Promise<void>;
  disabled?: boolean;
  invalidProps: string[];
  flow: string;
}

interface IBPBrokerFeeQuestions {
  obj: any;
  setObj: React.Dispatch<React.SetStateAction<any>>;
  handleFormChange: (value: any, field: any, obj: any, setObj: React.Dispatch<any>, type?: string, handleCustomFormChange?: any, charLimit?: number | undefined) => void;
  handleCustomFormChange?: (field: any, value: any, formObj: any) => void;
  disabled?: boolean;
  invalidProps: string[];
  flow: string;
  metadata?: any;
}

interface IBPSelectLoanOfficerQuestion {
  obj: any;
  setObj: React.Dispatch<React.SetStateAction<any>>;
  value: string | boolean;
  dropdownOptions: any[];
  handleFormChange: (value: any, field: any, obj: any, setObj: React.Dispatch<any>, type?: string, handleCustomFormChange?: any, charLimit?: number | undefined) => void;
  disabled?: boolean;
  invalidProps: string[];
  flow: string;
  required?: boolean;
}

export const YesNoRadios = [
  {
    name: "Yes",
    value: true
  },
  {
    name: "No",
    value: false
  }
];

export const BPAssociatedBrokerQuestion = ({ obj, handleFormChange, disabled, invalidProps, flow }: IBPAssociatedBrokerQuestion) => {
  return (
    <Grid.Column width={16}>
      {FeatureFlags.IS_BETA && <React.Fragment>
        <BCFormInput
          inputKey={`${flow}-broker`}
          type="radio"
          inputArray={YesNoRadios}
          label={BPQuestionLabels.BrokerAssociated}
          obj={obj}
          field="isBrokerInvolved"
          valueField="value"
          handleFormChange={(value, field) => handleFormChange(value, field)}
          required
          disabled={disabled}
        />
        <BCValidationMessage invalidProps={invalidProps} prop={"isBrokerInvolved"} />
      </React.Fragment>}
    </Grid.Column>
  );
};

export const BPBrokerEmailQuestion = ({
  isBrokerSelected,
  handleAddBroker,
  handleOnSearchChange,
  obj,
  value,
  handleRenderSuggestions,
  handleSearchSelect,
  isSearchOpen,
  onSearchFocus,
  handleSearchKeyDown,
  disabled,
  invalidProps,
  flow
}: IBPBrokerEmailQuestion) => {
  return (
    <Grid.Column width={16}>
      {(FeatureFlags.IS_BETA && obj.isBrokerInvolved === true) && <React.Fragment>
        <BCFormInput
          inputKey={`${flow}-broker`}
          actionLabel="What is the broker's email address?"
          actionText={(!isBrokerSelected && value !== "") ? "+ Add New Broker" : ""}
          label={isBrokerSelected || value === "" ? "What is the broker's email address?" : ""}
          labelAction={() => handleAddBroker()}
          type={GlobalConstants.FormInputTypes.SEARCH_FILTER}
          onSearchChange={(value, field) => handleOnSearchChange(value, field)}
          obj={obj}
          value={value}
          field="brokerSearchEmail"
          renderSuggestions={handleRenderSuggestions()}
          handleSearchSelect={(e, data) => handleSearchSelect(data.result)}
          isSearchOpen={isSearchOpen}
          onSearchFocus={() => onSearchFocus(true)}
          noSearchResultsMessage="To add a new Broker, type their email address and press enter."
          searchClass="bc-search-input-full"
          handleSearchKeyDown={handleSearchKeyDown}
          required
          disabled={disabled}
        />
        <BCValidationMessage invalidProps={invalidProps} prop={"brokerOrganizationContactId"} />
        <BCValidationMessage invalidProps={invalidProps} prop={"brokerOrganizationContactIdError"} propText="Please re-enter a valid broker email and press enter."/>
        <BCValidationMessage 
          invalidProps={invalidProps} 
          prop="custom-broker-invalid-email"
          propText="This email is invalid."
        />
      </React.Fragment>}
    </Grid.Column>
  );
};

export const BPBrokerFeeRateQuestion = ({ obj, setObj, handleFormChange, handleCustomFormChange, disabled, invalidProps, flow, metadata }: IBPBrokerFeeQuestions) => {
  let minMandatoryBrokerFee = ConfigDefaults.MIN_MANDATORY_BROKER_FEE;
  if (metadata && metadata.configs) {
    minMandatoryBrokerFee = metadata.configs.minMandatoryBrokerFee;
  }
  
  return (
    <>
      <Grid.Column width={16}>
        <React.Fragment>
          <BCFormInput
            type="input"
            inputKey={`${flow}-standard-broker-fee`}
            label={FeatureFlags.IS_BROKER_FEE_ALLIANCE ? BPQuestionLabels.BrokerFeeRate : BPQuestionLabels.BrokerFeeRateRebate}
            placeholder="1"
            obj={obj}
            field="brokerFeeRate"
            inputType="number"
            fieldType={GlobalConstants.InputTypes.PERCENTAGE}
            unitType="%"
            handleFormChange={(value, field) => handleFormChange(value, field, obj, setObj, GlobalConstants.InputTypes.PERCENTAGE, handleCustomFormChange)}
            required
            disabled={disabled}
          />
          <BCValidationMessage invalidProps={invalidProps} prop={"brokerFeeRate"} />
        </React.Fragment>
      </Grid.Column>
      {FeatureFlags.IS_BROKER_FEE_ALLIANCE ? <>
        <Grid.Column width={16}>
          <React.Fragment>
            <BCFormInput
              type="input"
              inputKey={`${flow}-wl-broker-fee`}
              label={BPQuestionLabels.WlBrokerFeeRate}
              placeholder="1"
              obj={obj}
              field="wlbrokerFeeRate"
              inputType="number"
              fieldType={GlobalConstants.InputTypes.PERCENTAGE}
              unitType="%"
              handleFormChange={(value, field) => handleFormChange(value, field, obj, setObj, GlobalConstants.InputTypes.WL_BROKER_FEE, handleCustomFormChange)}
              required
              disabled={disabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop={"wlbrokerFeeRate"} />
          </React.Fragment>
        </Grid.Column>
        <Grid.Column width={16}>
          <React.Fragment>
            <BCFormInput
              type="input"
              inputKey={`${flow}-min-mandatory-fee`}
              label={BPQuestionLabels.MinMandatoryBrokerFeeRate}
              placeholder="1"
              obj={obj}
              field="minMandatoryBrokerFeeRate"
              inputType="number"
              fieldType={GlobalConstants.InputTypes.PERCENTAGE}
              unitType="%"
              value={minMandatoryBrokerFee}
              disabled
            />
          </React.Fragment>
        </Grid.Column>
        <Grid.Column width={16}>
          <React.Fragment>
            <BCFormInput
              type="input"
              inputKey={`${flow}-min-mandatory-fee`}
              label={BPQuestionLabels.TotalBrokerFeeRate}
              placeholder="1"
              obj={obj}
              field="brokerFeeRate"
              inputType="number"
              fieldType={GlobalConstants.InputTypes.PERCENTAGE}
              unitType="%"
              value={operationOfNumberArray("sum", [obj.brokerFeeRate, obj.wlbrokerFeeRate, minMandatoryBrokerFee])}
              disabled
            />
          </React.Fragment>
        </Grid.Column>
      </> : <Grid.Column width={16}>
        <React.Fragment>
          <BCFormInput 
            type="radio"
            inputKey={`${flow}-combine-origination-fee`}
            inputArray={YesNoBooleanRadios} 
            label={BPQuestionLabels.CombineOriginationFeeRate}
            obj={obj}
            field="isCombineOriginationFee"
            valueField="value"
            handleFormChange={(value, field) => handleFormChange(value, field, obj, setObj)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="isCombineOriginationFee" />
        </React.Fragment>
      </Grid.Column>}
    </>
  );
};

export const BPSelectLoanOfficerQuestion = ({ obj, setObj, value, dropdownOptions, handleFormChange, disabled, invalidProps, flow, required }: IBPSelectLoanOfficerQuestion) => {
  return (
    <Grid.Column width={16}>
      <React.Fragment>
        <BCFormInput
          type="dropdown"
          inputKey={`${flow}-loan-officer`}
          label="Select loan officer"
          placeholder="Please Select"
          obj={obj}
          value={value}
          field="loanOfficerOrganizationContactId"
          dropdownOptions={dropdownOptions}
          handleFormChange={(value, field) => handleFormChange(value, field, obj, setObj)}
          required={required}
          disabled={disabled}
        />
        <BCValidationMessage invalidProps={invalidProps} prop={"loanOfficerOrganizationContactId"} />
      </React.Fragment>
    </Grid.Column>
  );
};

export const BPBorrowerEntitySelection = ({ 
  obj,
  handleApplicationFormChange,
  handleBorrowerFormChange, 
  isDisabled = null,
  dropdownOptions,
  invalidProps,
  brokerColor = null
}) => {
  return (
    <Grid.Row className="application-row">
      <Grid.Column width={16}>
        <BCFormInput labelClass={`primary-title-label form-input-header ${getIsWhiteLabeled() ? getBrandedStyle(BrandStyles.BrokerLabelBorderOverrideCSS, brokerColor) : "form-input-border"}`} label={`Borrower Information${obj.name ? ` for ${obj.name}` : ""}`} />
      </Grid.Column>
      <Grid.Column width={16}>
        <BCFormInput 
          type="radio"
          inputKey="entity"
          inputArray={YesNoBooleanRadios} 
          label={getIsWhiteLabeled() ? "Has the Borrowing Entity worked with this lender on a Previous Project?" : "Has the Borrowing Entity for this Project worked with Construction Loan Services II before?"}
          obj={obj}
          field="isExistingBorrower"
          valueField="value"
          handleFormChange={(value, field) => handleApplicationFormChange(value, field)}
          disabled={isDisabled}
          required
        />
        <BCValidationMessage invalidProps={invalidProps} prop="isExistingBorrower" />
      </Grid.Column>
      {obj.isExistingBorrower === true && <Grid.Column width={16}>
        <BCFormInput
          type="dropdown"
          inputKey="entity"
          label={BPQuestionLabels.BorrowingEntity}
          placeholder="Please Select"
          obj={obj}
          field="borrowerOrganizationId"
          dropdownOptions={dropdownOptions}
          handleFormChange={(value, field) => handleBorrowerFormChange(value, field)}
          disabled={isDisabled}
          required
        />
        <BCValidationMessage invalidProps={invalidProps} prop="borrowerOrganizationId" />
      </Grid.Column>}
    </Grid.Row>
  );
};

export const BPBorrowerEntityAdditionalInformation = ({
  obj,
  brokerColor,
  isGreaterThanMobile,
  entityObj,
  setEntityObj,
  handleEntityFormChange,
  isDisabled = null,
  invalidProps,
  entityDropdownOptions,
  isExtended = false
}) => {
  return (
    <React.Fragment>
      <Grid.Row className="application-row">
        <Grid.Column width={16}>
          <BCFormInput labelClass={`primary-title-label form-input-header ${getIsWhiteLabeled() ? getBrandedStyle(BrandStyles.BrokerLabelBorderOverrideCSS, brokerColor) : "form-input-border"}`} label={`Borrower Information${obj.name ? ` for ${obj.name}` : ""}`} />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row">
        <Grid.Column width={isGreaterThanMobile ? 10 : 16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            inputKey="entity"
            label="Borrower (Business) Name"
            placeholder="Construction Loan Services II, LLC"
            obj={entityObj}
            field="name"
            inputType="text"
            handleFormChange={(value, field) => handleEntityFormChange(value, field, GlobalConstants.InputTypes.ORGANIZATION, 50)}
            disabled={isDisabled}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="name" />
        </Grid.Column>
        <Grid.Column width={isGreaterThanMobile ? 6 : 16}>
          <BCFormInput 
            labelClass="gmr-background-label"
            type="dropdown"
            inputKey="entity"
            label="Type"
            placeholder="Please Select"
            obj={entityObj}
            field="entitySubTypeId"
            dropdownOptions={entityDropdownOptions}
            handleFormChange={(value, field) => handleEntityFormChange(value, field)}
            disabled={isDisabled}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="entitySubTypeId" />
        </Grid.Column>
        <Grid.Column width={16}>
          <BCFormInput
            label="Address"
            inputKey="entity"
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.PLACES}
            placeDetailsObject={entityObj}
            setPlaceDetailsObject={setEntityObj}
            placeholder="1234 Main Street, Seattle, WA 98052, USA"
            defaultValue={handleAddressValue({
              address: entityObj.physicalStreet,
              city: entityObj.physicalCityName,
              state: entityObj.physicalStateName,
              zip: entityObj.physicalZip
            })}
            placeFields={["address", "city", "state", "county", "zip"]}
            placeFieldsNameOverrides={[
              {
                addressComponent: "address",
                field: "physicalStreet"
              },
              {
                addressComponent: "city",
                field: "physicalCityName"
              },
              {
                addressComponent: "state",
                field: "physicalStateName"
              },
              {
                addressComponent: "county",
                field: "physicalCountyName"
              },
              {
                addressComponent: "zip",
                field: "physicalZipCode"
              },
            ]}
            disabled={isDisabled}
            required
          />
          {invalidProps.includes("physicalCityName") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalCityName" 
            propText="This city is invalid." 
          /> : invalidProps.includes("physicalStateName") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalStateName"
            propText="This state is invalid."
          /> : invalidProps.includes("physicalZipCode") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalZipCode"
            propText="This zip code is invalid."
          /> : <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalStreet" 
            propText={ErrorMessages.Address} />
          }
        </Grid.Column>
        {isExtended && <Grid.Column width={16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            label="Address Line 2"
            inputKey="entity"
            placeholder="Apartment, suite, unit, building, floor, etc."
            obj={entityObj}
            field="addressLine2"
            inputType="text"
            handleFormChange={(value, field) => handleEntityFormChange(value, field)}
            disabled={isDisabled}
          />
        </Grid.Column>}
      </Grid.Row>
      {isExtended && <React.Fragment>
        <Grid.Row className="application-row">
          <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              inputKey="entity"
              label="EIN/TIN"
              placeholder="474996817"
              obj={entityObj}
              field="eintin"
              inputType="text"
              handleFormChange={(value, field) => handleEntityFormChange(value, field, GlobalConstants.InputTypes.EIN)}
              disabled={isDisabled}
            />
            <BCValidationMessage invalidProps={invalidProps} prop="eintin" propText={"EIN/TIN must be greater than 9 numbers."}/>
          </Grid.Column>
          <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              inputKey="entity"
              label="Website"
              placeholder="https://serviceyourloan.com"
              obj={entityObj}
              field="website"
              inputType="text"
              handleFormChange={(value, field) => handleEntityFormChange(value, field, undefined, 2048)}
              disabled={isDisabled}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="application-row">
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              inputKey="entity"
              label="Years In Business"
              placeholder="5"
              obj={entityObj}
              field="yearsInBusiness"
              inputType="text"
              handleFormChange={(value, field) => handleEntityFormChange(value, field, GlobalConstants.InputTypes.INT)}
              disabled={isDisabled}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              inputKey="entity"
              label={`Gross Revenue${isGreaterThanMobile && " (Two Year Average)"}`}
              placeholder="10000"
              obj={entityObj}
              field="grossRevenue"
              fieldType="dollar"
              inputType="number"
              handleFormChange={(value, field) => handleEntityFormChange(value, field, GlobalConstants.InputTypes.DOLLAR)}
              disabled={isDisabled}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <BCFormInput
              labelClass="gmr-background-label"
              type="input"
              inputKey="entity"
              label={`Net Income${isGreaterThanMobile && " (Two Year Average)"}`}
              placeholder="10000"
              obj={entityObj}
              field="netIncome"
              fieldType="dollar"
              inputType="number"
              handleFormChange={(value, field) => handleEntityFormChange(value, field, GlobalConstants.InputTypes.DOLLAR)}
              disabled={isDisabled}
            />
          </Grid.Column>
        </Grid.Row>
      </React.Fragment>}
    </React.Fragment>
  );
};